import React, { createContext, useContext, useState, useEffect } from 'react';
import AxiosUrl from '../Component/AxiosUrl';

const BusinessSettingsContext = createContext();

export const useBusinessSettings = () => {
    const context = useContext(BusinessSettingsContext);
    if (!context) {
        throw new Error('useBusinessSettings must be used within BusinessSettingsProvider');
    }
    return context;
};

export const BusinessSettingsProvider = ({ children }) => {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const uid = localStorage.getItem('id');

    // Fetch settings on mount
    useEffect(() => {
        if (uid) {
            fetchSettings();
        } else {
            setLoading(false);
        }
    }, [uid]);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const response = await AxiosUrl.get(`/business-settings/${uid}`);

            if (response.data.success) {
                setSettings(response.data.data);
                // Cache in localStorage for quick access
                localStorage.setItem('businessSettings', JSON.stringify(response.data.data));
            } else {
                setError(response.data.error);
            }
        } catch (err) {
            console.error('Error fetching business settings:', err);
            setError(err.message);

            // Try to load from cache if API fails
            const cached = localStorage.getItem('businessSettings');
            if (cached) {
                setSettings(JSON.parse(cached));
            }
        } finally {
            setLoading(false);
        }
    };

    const updateSettings = async (newSettings) => {
        try {
            const response = await AxiosUrl.post(`/business-settings/${uid}`, {
                settings: newSettings,
            });

            if (response.data.success) {
                setSettings(response.data.data);
                // Update cache
                localStorage.setItem('businessSettings', JSON.stringify(response.data.data));
                return { success: true };
            } else {
                return { success: false, error: response.data.error };
            }
        } catch (err) {
            console.error('Error updating business settings:', err);
            return { success: false, error: err.message };
        }
    };

    // Helper functions to check specific features
    const isFeatureEnabled = (featureName) => {
        if (!settings || !settings.features) return false;
        return settings.features[featureName] === true;
    };

    const isSimpleMode = () => settings?.accountingMode === 'simple';
    const isStandardMode = () => settings?.accountingMode === 'standard';
    const isAdvancedMode = () => settings?.accountingMode === 'advanced';

    const isSingleOwner = () => settings?.businessType === 'single';
    const isPartnership = () => settings?.businessType === 'partnership';
    const isCompany = () => settings?.businessType === 'company';

    const value = {
        settings,
        loading,
        error,
        fetchSettings,
        updateSettings,
        // Helper functions
        isFeatureEnabled,
        isSimpleMode,
        isStandardMode,
        isAdvancedMode,
        isSingleOwner,
        isPartnership,
        isCompany,
    };

    return (
        <BusinessSettingsContext.Provider value={value}>
            {children}
        </BusinessSettingsContext.Provider>
    );
};

export default BusinessSettingsContext;
